import React, { useEffect, useState, useCallback, useRef, useMemo, useContext } from "react";
import { secureGet, secureDelete, securePost, secureGetPdf } from "../../config/api";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { formatDateTime } from "../../utils/format";
import Table from "../../components/Table";
import Filter from "../../components/Filter";
import OptionsMenu from "../../components/OptionsMenu";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";
import UserContext from "../../Shared/UserContext";

import EyeIcon from "../../icons/eye.svg";
import DeleteIcon from "../../icons/trashR.svg";
import DownloadIcon from "../../icons/downloadB.svg";
import checkIcon from "../../icons/checkB.svg";

const STATE_COLORS = {
  true: "#06D6A0",
  false: "#ADAFC6",
};

const DeliveryNotes = () => {
  const [deliveryNotesData, setDeliveryNotesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ clientId: null, commercialId: null, dateRange: {}, needle: "" });
  const [clients, setClients] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const topRef = useRef(null);
  const { state: { role } } = useContext(UserContext);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isCommercial = role === "ROLE_COMMERCIAL";

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  useEffect(() => {
    if (location.state?.showSnackbar) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const getStatutStyle = (isAvailable) => ({
    backgroundColor: isAvailable ? "#06D6A0" : "#EF476F",
    color: "white", padding: "5px 7px",
    borderRadius: "10px",
    textAlign: "center",
  });

  const renderStatut = (bl) => (
    <div style={{ display: "flex", alignItems: "center" }}>
      <span className="font-semibold text-[12px]" style={getStatutStyle(bl.carts?.[0]?.quotes?.length > 0)}>DV</span>
      <div className="w-1 h-[2px] bg-[#043F88]"></div>

      <span className="font-semibold text-[12px]" style={getStatutStyle(true)}>BC</span>
      <div className="w-1 h-[2px] bg-[#043F88]"></div>

      <span className="font-semibold text-[12px]" style={getStatutStyle(true)}>BL</span>
    </div>
  );

  const getStateLabel = (state) => t(`treated_status.${state}`);

  const columns = [
    { label: t("columns.deliveryNoteNumber"), key: "deliveryNoteNumber" },
    { label: t("columns.createdAt"), key: "createdAt", render: (date) => formatDateTime(date) },
    { label: "Client", key: "client", render: (client) => client?.name || "N/A" },
    { label: t("columns.commercial"), key: "user", render: (user) => `${user.firstName} ${user.lastName}` },
    { label: "Total HT", key: "priceHT" },
    { label: "Total TTC", key: "priceTTC" },
    { label: t("columns.status"), render: (_, rowData) => renderStatut(rowData) },
    {
      label: t("columns.state"), key: "treated",
      render: (treated) => {
        return (
          <span
            className="text-white text-[12px] font-semibold px-2 py-1 rounded-full inline-block w-fit text-center"
            style={{ backgroundColor: STATE_COLORS[treated] || "#A8ADC3" }}
          >
            {getStateLabel(treated).toUpperCase()}
          </span>
        );
      },
    },
    {
      key: "actions", render: (_, rowData) =>

        <div onClick={(e) => e.stopPropagation()}>
          <OptionsMenu options={getOptions(rowData)} />
        </div>
    },
  ];

  const getOptions = (item) => [
    { label: t("actions.details"), icon: EyeIcon, action: () => handleDetails(item) },
    ...(role === "ROLE_AGENT" && item.treated === false
      ? [{ label: t("actions.treat"), icon: checkIcon, action: () => handleTreat(item) }]
      : []
    ),
    { label: t("actions.downloadBL"), icon: DownloadIcon, action: () => handleDownloadClick(item.id) },
    { label: t("actions.delete"), color: "#EF476F", icon: DeleteIcon, action: () => handleDelete(item) },
  ];

  const filterOptions = [
    { name: "needle", type: "search" },
    { name: "dateRange", type: "daterange" },
    {
      name: "clientId",
      label: "Client",
      type: "dropdown",
      options: [...clients.map(client => ({ value: client.id, reference: client.code, name: client.name }))]
    },
    {
      name: "commercialId",
      label: t(`filters.orders.commercial`),
      type: "dropdown",
      options: isCommercial
        ? [
          {
            value: user.id,
            label: `${user.firstName} ${user.lastName}`,
          },
        ]
        : commercials.map((commercial) => ({
          value: commercial.id,
          label: `${commercial.firstName} ${commercial.lastName}`,
        })),
    },
    {
      name: "treated",
      label: t("filters.orders.treated"),
      type: "dropdown",
      options: [
        { value: "true", label: t("treated_status.true") },
        { value: "false", label: t("treated_status.false") }
      ]
    }
  ];

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const clientResponse = await secureGet("/clients", showSnackbar, { signal });
        setClients(clientResponse.data);

        if (!isCommercial) {
          const commercialResponse = await secureGet("/users/ROLE_COMMERCIAL", showSnackbar, { signal });
          setCommercials(commercialResponse.data);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching dropdown options:", error);
        }
      }
    };
    fetchDropdownOptions();

    return () => {
      abortController.abort();
    };
  }, [abortController, signal]);

  const fetchPaginatedDeliveryNotes = useCallback(async (page = 0, size = pageSize, activeFilters = filters) => {
    setLoading(true);

    try {
      const requestBody = {
        clientId: activeFilters.clientId || null,
        commercialId: activeFilters.commercialId || null,
        startDate: activeFilters.startDate || null,
        endDate: activeFilters.endDate || null,
        needle: activeFilters.needle || null,
        treated: activeFilters.treated || null,
      };


      const response = await securePost(`/deliveryNotes/all/paged`, requestBody, showSnackbar, { params: { page, size }, signal });
      const { content, totalPages } = response.data;
      setDeliveryNotesData(content);
      setTotalPages(totalPages);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching delivery notes:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize, signal]);

  useEffect(() => {
    if (location.state?.filters || location.state?.page || location.state?.size) {
      const restoredFilters = location.state.filters || {};
      const restoredPage = location.state.page || 0;
      const restoredSize = location.state.size || 30;

      setFilters(restoredFilters);
      setCurrentPage(restoredPage);
      setPageSize(restoredSize);

      fetchPaginatedDeliveryNotes(restoredPage, restoredSize, restoredFilters);
    } else {
      fetchPaginatedDeliveryNotes(0, pageSize, filters);
    }
  }, []);


  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedDeliveryNotes(page, pageSize, filters);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaginatedDeliveryNotes(0, size, filters);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      const { startDate, endDate } = filterValue || {};
      updatedFilters.startDate = startDate || "";
      updatedFilters.endDate = endDate || "";
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setCurrentPage(0);
    fetchPaginatedDeliveryNotes(0, pageSize, updatedFilters);
  };

  const resetFilters = () => {
    const defaultFilters = { clientId: null, commercialId: null, dateRange: {}, needle: "" };
    setFilters(defaultFilters);
    setCurrentPage(0);
    fetchPaginatedDeliveryNotes(0, pageSize, defaultFilters);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await secureDelete(`/deliveryNotes/delete/${itemToDelete.id}`, showSnackbar);
      setDeletePopupVisible(false);
      setItemToDelete(null);
      showSnackbar(t("confirmation.deliveryNotesDelete"), "success");
      fetchPaginatedDeliveryNotes(currentPage, pageSize);
    } catch (error) {
      console.error("Error deleting delivery note:", error);
    } finally {
      setDeleting(false);
      setDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

    const handleTreat = async (item) => {
    const payload = {
      deliveryNoteId: item.id,
      treated: true
    };
    try {
      const response = await securePost(`/deliveryNotes/updateTreated`, payload, showSnackbar);
      showSnackbar(t(`confirmation.treatSuccess`), 'success');
      fetchPaginatedDeliveryNotes(currentPage, pageSize, filters);
    } catch (error) {
      console.error("Error treating order:", error);
    }
  };

  const handleDetails = (item) => {
    history.push({
      pathname: `/delivery-notes/details/${item.id}`,
      state: {
        filters,
        page: currentPage,
        size: pageSize,
      },
    });
  };
  
  const handleDownloadClick = async (id) => {
    try {
      const response = await secureGetPdf(`/deliveryNotes/${id}/pdf`, showSnackbar);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Bon_Livraison_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t("confirmation.downloadSuccess"), "success");
    } catch (error) {
      console.error("Failed to download BL:", error);
    }
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.bl")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px]">
        <h1 className="text-[40px] font-semibold">{t("titles.bl")}</h1>
      </div>
      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />
      <div style={{ position: "relative", minHeight: "200px" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1
            }}>
            <LoadingLogo />
          </div>
        )}
        <Table columns={columns} data={deliveryNotesData}
          onRowClick={(row) => {
            history.push({
              pathname: `/delivery-notes/details/${row.id}`,
              state: {
                filters,
                page: currentPage,
                size: pageSize,
              },
            });
          }}
        />
      </div>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible &&
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default DeliveryNotes;
